import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft, CalendarDays, ChevronRight, CircleUserRound, Clock3, Home,
  Info, ListFilter, MapPin, Menu, Moon, Search, Shield, Sun, Tv, Users, X,
} from 'lucide-react';
import { applyScopedTheme, loadTeamLogoManifest, resolveScopedBrand, teamLogoAssetUrl } from '../../shared/app.js';
import { LeagueCenterShell } from './league-center/components/LeagueCenterShell';
import { GroupedStandings, Standings } from './league-center/components/StandingsTable';

declare global {
  interface Window { google?: any }
}

const NAV = [
  { id: 'home', label: 'Home', Icon: Home },
  { id: 'matches', label: 'Matches', Icon: CalendarDays },
  { id: 'teams', label: 'Teams', Icon: Users },
  { id: 'more', label: 'More', Icon: Menu },
];

const ROUTE_IDS = new Set([...NAV.map(({ id }) => id), 'table', 'players']);
const THEME_KEY = 'icat-theme';
const VIEWER_SEASON_KEY = 'icat-league-viewer-season';
const VIEWER_TEAM_KEY = 'icat-league-viewer-team';
const VIEWER_SETUP_KEY = 'icat-league-viewer-setup-complete';

const RESULT_STATUSES = new Set(['complete', 'abandoned', 'cancelled', 'no_result', 'forfeit']);
const GALLANTS_SPOTLIGHT_LOGO = '/client/assets/announcements/galle-gallants-logo-2026.png';
const GALLANTS_SPOTLIGHT_PHOTO = '/client/assets/announcements/kiran-mantri-lpl-trophy-photo-2026.png';

async function getJson(path, signal) {
  const response = await fetch(path, { signal, headers: { accept: 'application/json' } });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Could not load cricket data (${response.status})`);
  return body;
}

function normalizeLookup(value) {
  return String(value || '').toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, ' ').trim();
}

function staticStatsPathForSeason(seasonId) {
  if (seasonId === 's-ft20-2026') return '/client/assets/stats/ft20-2026-cricheroes-stats.json';
  return '';
}

async function loadStaticSeasonStats(data, signal) {
  const path = staticStatsPathForSeason(data?.season?.id);
  if (!path) return null;
  try {
    return await getJson(path, signal);
  } catch {
    return null;
  }
}

function normalizeExternalLeaderboardRows(data, rows = [], mode = '') {
  const teamsByLookup = new Map();
  for (const team of data.teams || []) {
    [team.name, team.shortName].filter(Boolean).forEach((value) => teamsByLookup.set(normalizeLookup(value), team));
  }
  const normalizedRows = [];
  if (mode === 'mvp') {
    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index];
      const next = rows[index + 1];
      const bat = String(next?.name || '').match(/^Bat:\s*(.+)$/i);
      const bowl = String(next?.team || next?.teamName || '').match(/^\+?\s*Bowl:\s*(.+)$/i);
      const field = String(next?.points || next?.Points || '').match(/^\+?\s*Field:\s*(.+)$/i);
      if (/^Bat:\s*/i.test(String(row?.name || ''))) continue;
      normalizedRows.push(bat && bowl && field
        ? { ...row, Bat: bat[1], Bowl: bowl[1], Field: field[1] }
        : row);
      if (bat && bowl && field) index += 1;
    }
  } else {
    normalizedRows.push(...rows);
  }
  return normalizedRows.map((row, index) => {
    const team = teamsByLookup.get(normalizeLookup(row.team || row.teamName)) || {};
    const base = {
      ...row,
      rank: mode === 'mvp' ? index + 1 : Number(row.rank || index + 1),
      name: row.name || row.playerName || row.fullName || '',
      playerName: row.name || row.playerName || row.fullName || '',
      teamId: team.id || row.teamId || '',
      teamName: team.name || row.team || row.teamName || '',
      teamShort: team.shortName || row.teamShort || '',
    };
    if (mode === 'batting') {
      return { ...base, innings: row.Inn || row.innings, runs: row.Runs || row.runs, best: row.Hs || row.HS || row.best, average: row.Avg || row.average, strikeRate: row.SR || row.strikeRate };
    }
    if (mode === 'bowling') {
      return { ...base, innings: row.Inn || row.innings, wickets: row.W || row.Wkts || row.wickets, economy: row.Eco || row.economy, average: row.Avg || row.average, dots: row.Dots || row.dots };
    }
    if (mode === 'fielding') {
      return { ...base, matches: row.Mat || row.matches, dismissals: row.Dismissal || row.dismissals, catches: row.Catches || row.catches, runOuts: row['R/O'] || row.runOuts, stumpings: row['St.'] || row.stumpings };
    }
    return { ...base, points: row.points || row.Points, bat: row.Bat || row.bat, bowl: row.Bowl || row.bowl, field: row.Field || row.field };
  });
}

function mergeExternalSeasonStats(data, stats) {
  if (!data?.season || !stats) return data;
  const next = { ...data };
  const teamsByLookup = new Map();
  for (const team of data.teams || []) {
    [team.name, team.shortName].filter(Boolean).forEach((value) => teamsByLookup.set(normalizeLookup(value), team));
  }
  if (Array.isArray(stats.pointTable) && stats.pointTable.length) {
    const groups = stats.pointTable.map((group) => ({
      name: group.name || '',
      stage: group.stage || 'League matches',
      rows: (group.rows || []).map((row) => {
        const team = teamsByLookup.get(normalizeLookup(row.team)) || {};
        return {
          teamId: team.id || '',
          name: team.name || row.team,
          shortName: team.shortName || '',
          groupName: group.name || '',
          groupStage: group.stage || 'League matches',
          groupRank: Number(row.rank || 0),
          rank: Number(row.rank || 0),
          played: Number(row.played || 0),
          won: Number(row.won || 0),
          lost: Number(row.lost || 0),
          drawn: Number(row.drawn || 0),
          tied: Number(row.tied || 0),
          noResult: Number(row.noResult || 0),
          nrr: row.nrr === null || row.nrr === undefined ? null : String(row.nrr),
          quotientFor: row.quotientFor || '',
          quotientAgainst: row.quotientAgainst || '',
          points: Number(row.points || 0),
          last5: row.last5 || '',
        };
      }).sort((a, b) => a.rank - b.rank),
    })).filter((group) => group.rows.length);
    if (groups.length) {
      next.standings = groups.flatMap((group) => group.rows);
      next.groupStats = { ...(data.groupStats || {}), groups };
    }
  }
  if (stats.leaderboard && typeof stats.leaderboard === 'object') {
    next.playerStats = {
      ...(data.playerStats || {}),
      batting: normalizeExternalLeaderboardRows(data, stats.leaderboard.batting || [], 'batting'),
      bowling: normalizeExternalLeaderboardRows(data, stats.leaderboard.bowling || [], 'bowling'),
      fielding: normalizeExternalLeaderboardRows(data, stats.leaderboard.fielding || [], 'fielding'),
      mvp: normalizeExternalLeaderboardRows(data, stats.leaderboard.mvp || [], 'mvp'),
    };
  }
  return { ...next, externalStats: stats };
}

function useLeagueData(seasonId) {
  const [state, setState] = useState({ status: 'loading', data: null, error: '' });
  useEffect(() => {
    const controller = new AbortController();
    setState((current) => ({ ...current, status: 'loading', error: '' }));
    const path = seasonId ? `/api/platform/public?seasonId=${encodeURIComponent(seasonId)}` : '/api/platform/public';
    getJson(path, controller.signal)
      .then(async (data) => mergeExternalSeasonStats(data, await loadStaticSeasonStats(data, controller.signal)))
      .then((data) => setState({ status: 'ready', data, error: '' }))
      .catch((error) => {
        if (error.name !== 'AbortError') setState({ status: 'error', data: null, error: error.message });
      });
    return () => controller.abort();
  }, [seasonId]);
  return state;
}

function getRoute(hash = location.hash) {
  const route = hash.slice(1).split('?')[0].split('/')[0];
  return ROUTE_IDS.has(route) ? route : 'home';
}

function getRouteParts(hash = location.hash) {
  return hash.slice(1).split('?')[0].split('/').filter(Boolean);
}

function getHashParams(hash = location.hash) {
  return new URLSearchParams(hash.slice(1).split('?')[1] || '');
}

function fixtureIdFromPath(pathname = location.pathname) {
  const [root, fixtureId] = pathname.split('/').filter(Boolean);
  return root === 'league' && fixtureId ? decodeURIComponent(fixtureId) : '';
}

function brandFor(data) {
  return resolveScopedBrand({
    season: data?.season?.brandingScope?.season || data?.season,
    league: data?.season ? {
      id: data.season.leagueId,
      name: data.season.leagueName,
      shortName: data.season.leagueShortName,
      theme: data.season.brandingScope?.league?.theme,
    } : null,
    fallbackSources: [data?.season?.leagueShortName, data?.season?.leagueName, data?.season?.name],
  });
}

function themeScopeForData(data) {
  if (!data?.season) return {};
  return {
    season: data.season.brandingScope?.season || data.season,
    league: data.season.brandingScope?.league || {
      id: data.season.leagueId,
      name: data.season.leagueName,
      shortName: data.season.leagueShortName,
    },
    fallbackSources: [data.season.leagueShortName, data.season.leagueName, data.season.name],
  };
}

function useLeagueTheme() {
  const [dark, setDark] = useState(() => localStorage.getItem(THEME_KEY) === 'dark');
  useEffect(() => {
    document.body.classList.toggle('dark', dark);
    localStorage.setItem(THEME_KEY, dark ? 'dark' : 'light');
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      const themed = getComputedStyle(document.body).getPropertyValue('--theme-color').trim();
      meta.setAttribute('content', themed || (dark ? '#101820' : '#ffebda'));
    }
  }, [dark]);
  return { dark, setDark };
}

function useLiveScoreSummaries(data) {
  return useMemo(() => ({}), [data?.season?.id]);
}

function statusOf(fixture) {
  if (fixture?.matchId && !fixture?.preprovisioned && ['scheduled', 'rain_delay', 'postponed'].includes(fixture.status)) return 'live';
  return fixture?.status || 'scheduled';
}

function matchFilterHref(fixture) {
  const status = statusOf(fixture);
  if (status === 'live') return '/#matches?filter=live';
  if (RESULT_STATUSES.has(status)) return '/#matches?filter=results';
  return '/#matches?filter=upcoming';
}

function matchCardHref(fixture) {
  const status = statusOf(fixture);
  if (fixture?.matchId && (status === 'live' || RESULT_STATUSES.has(status))) {
    return `/live/${encodeURIComponent(fixture.matchId)}`;
  }
  return `/league/${encodeURIComponent(fixture.id)}`;
}

function fixtureHasTeam(fixture, teamId) {
  if (!teamId) return true;
  return fixture.teamAId === teamId || fixture.teamBId === teamId;
}

function dateValue(fixture) {
  return Date.parse(`${fixture.date || '1970-01-01'}T${fixture.startTime || '00:00'}`) || 0;
}

function formatDate(value, options = {}) {
  if (!value) return 'Date pending';
  const [year, month, day] = String(value).split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Intl.DateTimeFormat(undefined, {
    weekday: options.weekday === false ? undefined : 'short',
    month: 'short', day: 'numeric', year: options.year ? 'numeric' : undefined,
  }).format(new Date(year, month - 1, day));
}

function isWeekendFixture(fixture) {
  if (!fixture?.date) return false;
  const [year, month, day] = String(fixture.date).split('-').map(Number);
  if (!year || !month || !day) return false;
  const weekday = new Date(year, month - 1, day).getDay();
  return weekday === 0 || weekday === 6;
}

function localDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function immediateWeekendDates(now = new Date()) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekday = today.getDay();
  const saturdayOffset = weekday === 0 ? -1 : (6 - weekday + 7) % 7;
  const saturday = new Date(today);
  saturday.setDate(today.getDate() + saturdayOffset);
  const sunday = new Date(saturday);
  sunday.setDate(saturday.getDate() + 1);
  return new Set([localDateKey(saturday), localDateKey(sunday)]);
}

function cleanSpotlightText(value) {
  return String(value || '').replace(/^ICAT community spotlight:\s*/i, '').trim();
}

function formatMonth(value) {
  if (!value) return 'Date pending';
  const [year, month] = String(value).split('-').map(Number);
  if (!year || !month) return value;
  return new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(new Date(year, month - 1, 1));
}

function formatTime(value) {
  if (!value) return '';
  const [hours, minutes = '00'] = value.split(':').map(Number);
  if (!Number.isFinite(hours)) return value;
  return `${hours % 12 || 12}:${String(minutes).padStart(2, '0')} ${hours >= 12 ? 'PM' : 'AM'}`;
}

function shortSeason(season) { return season?.name || 'Current season'; }
function teamFrom(data, id, fallback = {}) { return data.teams.find((team) => team.id === id) || fallback; }
function playerName(player) { return player?.fullName || player?.name || 'Player'; }
function initials(value) {
  return String(value || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || '?';
}

function enrichRowLogo(row, teamById, manifest, brand) {
  const team = teamById.get(row.teamId || row.id) || {};
  const logoUrl = row.logoUrl || team.logoUrl || teamLogoAssetUrl({ ...team, ...row }, manifest, brand);
  return { ...row, logoUrl };
}

function useLogoEnrichedData(data) {
  const [manifestState, setManifestState] = useState({ key: '', manifest: { teams: [] } });
  const brand = useMemo(() => brandFor(data), [data]);
  useEffect(() => {
    if (!data?.season || !brand?.teamLogoManifestPath) return;
    let cancelled = false;
    loadTeamLogoManifest(brand).then((manifest) => {
      if (!cancelled) setManifestState({ key: brand.teamLogoManifestPath, manifest });
    });
    return () => { cancelled = true; };
  }, [brand?.teamLogoManifestPath, data?.season?.id]);

  return useMemo(() => {
    if (!data?.season) return data;
    const manifest = manifestState.key === brand?.teamLogoManifestPath ? manifestState.manifest : { teams: [] };
    const teams = data.teams.map((team) => ({
      ...team,
      logoUrl: team.logoUrl || teamLogoAssetUrl(team, manifest, brand),
    }));
    const teamById = new Map(teams.map((team) => [team.id, team]));
    const standings = data.standings.map((row) => enrichRowLogo(row, teamById, manifest, brand));
    const groupStats = {
      ...(data.groupStats || {}),
      groups: (data.groupStats?.groups || []).map((group) => ({
        ...group,
        rows: (group.rows || []).map((row) => enrichRowLogo(row, teamById, manifest, brand)),
      })),
    };
    return { ...data, teams, standings, groupStats };
  }, [data, manifestState, brand]);
}

function TeamMark({ team, size = 40 }) {
  const label = team.shortName || team.short || team.name || '?';
  const [imageFailed, setImageFailed] = useState(false);
  if (team.logoUrl && !imageFailed) return <img className="team-mark" src={team.logoUrl} alt="" style={{ width: size, height: size }} onError={() => setImageFailed(true)} />;
  return <span className="team-mark team-mark--fallback" style={{ width: size, height: size }}>{label.slice(0, 1)}</span>;
}

function playerPhotoUrl(player = {}) {
  return player.photoUrl
    || player.profilePhotoUrl
    || player.profileImageUrl
    || player.avatarUrl
    || player.headshotUrl
    || player.imageUrl
    || player.pictureUrl
    || '';
}

function playerFromStatRow(data, row) {
  const clean = (value) => String(value || '').trim().toLowerCase();
  const playerId = row?.playerId || row?.id || '';
  if (playerId) {
    const player = (data.players || []).find((item) => item.id === playerId || item.playerId === playerId)
      || (data.rosters || []).find((item) => item.playerId === playerId);
    if (player) return { ...player, ...row };
  }
  const name = clean(statValue(row, ['name', 'playerName', 'fullName']));
  if (!name) return row || {};
  const player = (data.players || []).find((item) => clean(playerName(item)) === name)
    || (data.rosters || []).find((item) => clean(playerName(item)) === name);
  return { ...(player || {}), ...(row || {}) };
}

function PlayerAvatar({ player, size = 36 }) {
  const label = playerName(player);
  const [imageFailed, setImageFailed] = useState(false);
  const photo = playerPhotoUrl(player);
  if (photo && !imageFailed) {
    return <img className="player-photo" src={photo} alt="" style={{ width: size, height: size }} loading="lazy" onError={() => setImageFailed(true)} />;
  }
  return <span className="player-photo player-photo--fallback" style={{ width: size, height: size }}>{initials(label)}</span>;
}

function fixtureStatusLabel(fixture, status) {
  if (status === 'live' && fixture.externalResult?.sourceStatus === 'break') return 'Innings break';
  if (status === 'scheduled') return fixture.stageName || fixture.groupName || 'League Matches';
  return status === 'complete' ? 'Final' : status.replaceAll('_', ' ');
}

function fixtureDisplayStatus(fixture, status) {
  if (status === 'live' && fixture.externalResult?.sourceStatus === 'break') return 'break';
  return status;
}

function StatusPill({ status, label }) {
  const text = label || (status === 'complete' ? 'Final' : status.replaceAll('_', ' '));
  return <span className={`status status--${status}`}>{status === 'live' && text.toLowerCase() === 'live' && <span className="live-dot" />}{text}</span>;
}

function Eyebrow({ children }) { return <span className="eyebrow">{children}</span>; }

function SectionTitle({ eyebrow, title, action }) {
  return <div className="section-title"><div><Eyebrow>{eyebrow}</Eyebrow><h2>{title}</h2></div>{action}</div>;
}

function StandingHeading({ title, stage, suffix = '' }) {
  return (
    <div className="standing-heading">
      <h2>{title}{suffix ? ` (${suffix})` : ''}</h2>
      {stage ? <em>{stage}</em> : null}
    </div>
  );
}

function accountInitials(user) {
  const label = String(user?.displayName || user?.email || '').trim();
  return label.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || '?';
}

function useViewerAccount() {
  const [account, setAccount] = useState({ status: 'loading', user: null, viewerPreferences: null, teamAccess: [] });
  useEffect(() => {
    const controller = new AbortController();
    getJson('/api/auth/me', controller.signal)
      .then((body) => setAccount({ status: 'ready', user: body.user || null, viewerPreferences: body.viewerPreferences || null, teamAccess: body.teamAccess || [] }))
      .catch((error) => {
        if (error.name !== 'AbortError') setAccount({ status: 'ready', user: null, viewerPreferences: null, teamAccess: [] });
      });
    return () => controller.abort();
  }, []);
  return account;
}

function AppHeader({ data, account, onSeasonChange, selectedTeamId, onTeamChange, onClearTeam, dark, setDark }) {
  const season = data.season;
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [logoutBusy, setLogoutBusy] = useState(false);
  const selectedTeam = teamFrom(data, selectedTeamId, null);
  const user = account?.user;
  const isAdmin = (user?.roles || []).some((role) => role.role === 'global_admin');
  const contextLabel = selectedTeam?.id
    ? `${shortSeason(season)} -> ${selectedTeam.shortName || selectedTeam.name}`
    : `${shortSeason(season)} -> All teams`;
  const changeSeason = (nextId) => {
    onSeasonChange(nextId);
  };
  const logout = async () => {
    setLogoutBusy(true);
    try {
      await fetch('/api/auth/logout', { method: 'POST', headers: { accept: 'application/json' } });
    } finally {
      location.href = '/';
    }
  };
  return (
    <header className="topbar">
      {(contextMenuOpen || accountMenuOpen) && <button type="button" className="menu-scrim" aria-label="Close menu" onClick={() => { setContextMenuOpen(false); setAccountMenuOpen(false); }} />}
      <div className="topbar__inner">
        <a className="brand" href="/#home" aria-label="ICAT Cricket home">
          <img src="/client/assets/icat-primary.jpeg" alt="" />
          <span><b>ICAT</b><small>Cricket</small></span>
        </a>
        <div className="context-picker">
          <button type="button" className="context-pill" aria-expanded={contextMenuOpen} onClick={() => { setAccountMenuOpen(false); setContextMenuOpen((value) => !value); }}>
            <ListFilter size={15} /><span>{contextLabel}</span><ChevronRight size={15} />
          </button>
          {contextMenuOpen && <div className="topbar-menu context-menu context-sheet">
            <div className="context-sheet__head">
              <Eyebrow>Viewing</Eyebrow>
              <strong>{shortSeason(season)} · {selectedTeam?.id ? selectedTeam.shortName || selectedTeam.name : 'All teams'}</strong>
            </div>
            <label className="context-field">
              <span>Season</span>
              <select value={season?.id || ''} onChange={(event) => changeSeason(event.target.value)}>
                {data.seasons.map((item) => <option value={item.id} key={item.id}>{item.leagueShortName || item.leagueName || 'League'} · {shortSeason(item)}</option>)}
              </select>
            </label>
            <label className="context-field">
              <span>Team</span>
              <select value={selectedTeam?.id || ''} onChange={(event) => onTeamChange(event.target.value)}>
                <option value="">All teams</option>
                {data.teams.map((team) => <option value={team.id} key={team.id}>{team.shortName || team.name}</option>)}
              </select>
            </label>
            {selectedTeam?.id && <button type="button" className="context-sheet__clear" onClick={onClearTeam}><X size={16} /> Clear team</button>}
          </div>}
        </div>
        <div className="account-menu-wrap">
          <button type="button" className="theme-link" aria-label={dark ? 'Use light mode' : 'Use night mode'} title={dark ? 'Light mode' : 'Night mode'} onClick={() => setDark((value) => !value)}>
            {dark ? <Sun size={19} /> : <Moon size={19} />}
          </button>
          {user ? (
            <button type="button" className="account-trigger" aria-label={`Account for ${user.displayName || user.email}`} aria-expanded={accountMenuOpen} onClick={() => { setContextMenuOpen(false); setAccountMenuOpen((value) => !value); }}>
              <span className="account-avatar">{accountInitials(user)}</span>
              <span className="account-trigger__copy"><strong>{user.displayName || user.email}</strong><small>Signed in</small></span>
              <ChevronRight size={15} />
            </button>
          ) : (
            <a
              className="account-trigger account-trigger--signed-out"
              aria-label="Sign in to ICAT Cricket"
              href={`/login/?next=${encodeURIComponent(location.pathname + location.search + location.hash)}`}
            >
              <CircleUserRound /><span className="account-trigger__copy"><strong>Sign in</strong><small>Save your view</small></span>
            </a>
          )}
          {accountMenuOpen && user && <div className="topbar-menu account-menu">
            <div className="account-summary">
              <span className="account-avatar account-avatar--large">{accountInitials(user)}</span>
              <span><strong>{user.displayName || 'ICAT member'}</strong><small>{user.email}</small></span>
            </div>
            <div className="account-status"><span /> Signed in</div>
            <p className="account-preference-note">Your league and team selections are saved to this account.</p>
            {isAdmin && <a href="/admin">Open admin</a>}
            <button type="button" className="account-logout" disabled={logoutBusy} onClick={logout}>{logoutBusy ? 'Logging out…' : 'Log out'}</button>
          </div>}
        </div>
      </div>
      <div className="theme-stripe" />
    </header>
  );
}

function MainNav({ route, mobile = false }) {
  const activeRoute = ['table', 'players'].includes(route) ? 'teams' : route;
  return (
    <nav className={mobile ? 'mobile-nav' : 'desktop-nav'} aria-label="Main navigation">
      {NAV.map(({ id, label, Icon }) => (
        <a key={id} href={`/#${id}`} className={activeRoute === id ? 'active' : ''} aria-current={activeRoute === id ? 'page' : undefined}>
          <Icon size={mobile ? 21 : 18} /><span>{label}</span>
        </a>
      ))}
    </nav>
  );
}

function Score({ value, overs }) {
  if (!value) return <span className="score score--empty">—</span>;
  const [runs, wickets] = String(value).split('/');
  return <span className="score">{runs}{wickets !== undefined && <i>/{wickets}</i>}{overs && <small>({overs})</small>}</span>;
}

function inningForTeam(state, teamId, sideId) {
  const innings = (state?.innings || []).filter((inn) => !inn.superOver && (inn.battingTeamId === teamId || inn.battingTeamId === sideId));
  return innings[innings.length - 1] || null;
}

function scoreResultForFixture(fixture, liveScores = {}) {
  const external = fixture.externalResult || {};
  const liveState = fixture.matchId ? liveScores[fixture.matchId] : null;
  if (!liveState?.innings?.length) return external;
  const teamA = inningForTeam(liveState, fixture.teamAId, 'A');
  const teamB = inningForTeam(liveState, fixture.teamBId, 'B');
  return {
    ...external,
    teamAScore: teamA ? `${teamA.runs}/${teamA.wickets}` : external.teamAScore,
    teamAOvers: teamA?.oversText || teamA?.overs || external.teamAOvers,
    teamBScore: teamB ? `${teamB.runs}/${teamB.wickets}` : external.teamBScore,
    teamBOvers: teamB?.oversText || teamB?.overs || external.teamBOvers,
    resultText: liveState.result?.text || external.resultText,
  };
}

function MatchCard({ fixture, data, liveScores }) {
  const status = statusOf(fixture);
  const displayStatus = fixtureDisplayStatus(fixture, status);
  const teamA = teamFrom(data, fixture.teamAId, { name: fixture.teamAName, shortName: fixture.teamAShort, logoUrl: fixture.teamALogoUrl });
  const teamB = teamFrom(data, fixture.teamBId, { name: fixture.teamBName, shortName: fixture.teamBShort, logoUrl: fixture.teamBLogoUrl });
  const result = scoreResultForFixture(fixture, liveScores);
  return (
    <a className="match-card" href={matchCardHref(fixture)}>
      <div className="match-card__top"><span>{fixture.matchNo ? `Match ${fixture.matchNo}` : 'League match'}</span><StatusPill status={displayStatus} label={fixtureStatusLabel(fixture, status)} /></div>
      <div className="match-card__team"><TeamMark team={teamA} size={38} /><strong>{teamA.shortName || teamA.name || 'Team A'}</strong><Score value={result.teamAScore} overs={result.teamAOvers} /></div>
      <div className="match-card__team"><TeamMark team={teamB} size={38} /><strong>{teamB.shortName || teamB.name || 'Team B'}</strong><Score value={result.teamBScore} overs={result.teamBOvers} /></div>
      <div className="match-card__meta">
        <span><CalendarDays size={14} /> {formatDate(fixture.date)}</span>
        {fixture.startTime && <span><Clock3 size={14} /> {formatTime(fixture.startTime)}</span>}
        {(fixture.venueName || fixture.groundName) && <span><MapPin size={14} /> {fixture.venueName || fixture.groundName}</span>}
      </div>
      {result.resultText && <p className="match-card__result">{result.resultText}</p>}
      <ChevronRight className="match-card__arrow" size={18} />
    </a>
  );
}

function HomeScreen({ data, liveScores, selectedTeamId, onClearTeam }) {
  const now = Date.now();
  const selectedTeam = teamFrom(data, selectedTeamId, null);
  const scopedFixtures = data.fixtures.filter((fixture) => fixtureHasTeam(fixture, selectedTeam?.id));
  const live = scopedFixtures.filter((fixture) => statusOf(fixture) === 'live');
  const upcoming = scopedFixtures.filter((fixture) => !RESULT_STATUSES.has(statusOf(fixture)) && statusOf(fixture) !== 'live')
    .sort((a, b) => Math.abs(dateValue(a) - now) - Math.abs(dateValue(b) - now));
  const immediateWeekend = immediateWeekendDates(new Date(now));
  const weekendUpcoming = upcoming.filter((fixture) => immediateWeekend.has(fixture.date));
  const completed = scopedFixtures.filter((fixture) => RESULT_STATUSES.has(statusOf(fixture))).length;
  const season = data.season;
  const leagueLabel = season.leagueShortName || season.leagueName || 'ICAT';
  const brand = brandFor(data);
  const announcements = data.announcements || [];
  const hasSpotlight = announcements.length > 0;
  const [heroSlide, setHeroSlide] = useState(0);
  const [heroTouchStart, setHeroTouchStart] = useState(null);
  useEffect(() => {
    if (!hasSpotlight) setHeroSlide(0);
  }, [hasSpotlight]);
  const handleHeroTouchEnd = (event) => {
    if (!hasSpotlight || heroTouchStart === null) return;
    const delta = event.changedTouches[0]?.clientX - heroTouchStart;
    setHeroTouchStart(null);
    if (Math.abs(delta) < 42) return;
    setHeroSlide((current) => (delta < 0 ? Math.min(1, current + 1) : Math.max(0, current - 1)));
  };
  return (
    <div className="screen home-screen">
      <section
        className={`hero${hasSpotlight ? ' hero--carousel' : ''}${hasSpotlight ? (heroSlide === 0 ? ' hero--spotlight-active' : ' hero--default-active') : ''}`}
        onTouchStart={hasSpotlight ? (event) => setHeroTouchStart(event.touches[0]?.clientX ?? null) : undefined}
        onTouchEnd={hasSpotlight ? handleHeroTouchEnd : undefined}
      >
        {hasSpotlight && heroSlide === 0 ? (
          <HeroAnnouncements announcements={announcements} brand={brand} />
        ) : (
          <>
            <div className="hero__copy">
              <Eyebrow>{leagueLabel} · {shortSeason(season)}</Eyebrow>
              <h1>{selectedTeam?.shortName || season.leagueName || leagueLabel}</h1>
              <p>{selectedTeam?.id ? `${selectedTeam.name} fixtures, live scores and season standing.` : season.description || `${season.format || 'T20'} cricket, fixtures, live scores and season standings.`}</p>
              <div className="hero__actions"><a className="button button--primary" href="#matches">View matches</a><a className="button button--quiet" href="#teams">Standings</a></div>
            </div>
            <img className="hero__shield" src={brand.logoSrc} alt="" />
          </>
        )}
        {hasSpotlight ? (
          <div className="hero-carousel-dots" role="tablist" aria-label="Home hero slides">
            {[0, 1].map((index) => (
              <button
                type="button"
                key={index}
                className={heroSlide === index ? 'active' : ''}
                aria-label={index === 0 ? 'Show community spotlight' : 'Show FT20 overview'}
                aria-selected={heroSlide === index}
                onClick={() => setHeroSlide(index)}
              />
            ))}
          </div>
        ) : null}
      </section>
      <section className="season-stats" aria-label="Season summary">
        <a href="#teams"><strong>{selectedTeam?.id ? 1 : data.teams.length}</strong><span>{selectedTeam?.id ? 'Team' : 'Teams'}</span></a>
        <a href="#matches"><strong>{scopedFixtures.length}</strong><span>Fixtures</span></a>
        <a href="#matches?filter=results"><strong>{completed}</strong><span>Results</span></a>
      </section>
      {live.length ? <section><SectionTitle eyebrow="Live now" title={`${live.length} match${live.length === 1 ? '' : 'es'} in play`} action={<a className="text-link" href="#matches">All matches <ChevronRight size={16} /></a>} />
          <div className="match-rail" aria-label="Live matches">
            {live.map((fixture) => <MatchCard key={fixture.id} fixture={fixture} data={data} liveScores={liveScores} />)}
          </div>
      </section> : null}
      {weekendUpcoming.length ? <section><SectionTitle eyebrow="This weekend" title="Upcoming matches this weekend" action={<a className="text-link" href="#matches?filter=upcoming">All matches <ChevronRight size={16} /></a>} />
          <div className="match-rail weekend-match-rail" aria-label="Upcoming matches this weekend">
            {weekendUpcoming.map((fixture) => <MatchCard key={fixture.id} fixture={fixture} data={data} liveScores={liveScores} />)}
          </div>
        </section>
        : !live.length ? <EmptyState title="No fixtures this weekend" text="The immediate Saturday/Sunday schedule will appear here once published." /> : null}
      <HomeTopStandings data={data} selectedTeamId={selectedTeam?.id} />
      {!!announcements.length && <section><SectionTitle eyebrow="League desk" title="Announcements" /><div className="card-grid">{announcements.slice(0, 3).map((item) => <AnnouncementCard item={item} key={item.id} />)}</div></section>}
    </div>
  );
}

function HeroAnnouncements({ announcements = [], brand }) {
  const items = announcements.filter((item) => item?.title || item?.body);
  if (!items.length) return null;
  const primary = items.find((item) => item.id === 'announcement-galle-gallants-lpl-2026') || items[0];
  const photo = items.find((item) => item.id === 'announcement-kiran-mantri-lpl-trophy-2026')
    || items.find((item) => item.imageUrl && item.id !== primary.id)
    || (primary.id === 'announcement-galle-gallants-lpl-2026' ? { imageUrl: GALLANTS_SPOTLIGHT_PHOTO } : primary);
  const logoUrl = primary.id === 'announcement-galle-gallants-lpl-2026' ? (primary.imageUrl || GALLANTS_SPOTLIGHT_LOGO) : (primary.imageUrl || '');
  const heroImageUrl = photo.imageUrl || (primary.id === 'announcement-galle-gallants-lpl-2026' ? GALLANTS_SPOTLIGHT_PHOTO : primary.imageUrl) || '';
  return (
    <div className="hero-announcements hero-announcements--spotlight" aria-label="League announcement spotlight">
      <article className={`hero-announcement-slide hero-announcement-slide--feature${heroImageUrl ? ' hero-announcement-slide--media' : ''}`}>
        <div className="hero-announcement-slide__copy">
          {logoUrl ? (
            <div className="hero-announcement-brandline">
              <img src={logoUrl} alt="" onError={(event) => { event.currentTarget.style.display = 'none'; }} />
            </div>
          ) : null}
          <strong>{primary.title}</strong>
          {primary.body ? <p>{cleanSpotlightText(primary.body)}</p> : null}
        </div>
        {heroImageUrl ? (
          <img className="hero-announcement-main-image" src={heroImageUrl} alt="" onError={(event) => { event.currentTarget.style.display = 'none'; }} />
        ) : null}
      </article>
    </div>
  );
}

function AnnouncementCard({ item }) {
  return (
    <article className={`content-card announcement-card${item.imageUrl ? ' announcement-card--media' : ''}`}>
      {item.imageUrl ? <img src={item.imageUrl} alt="" onError={(event) => { event.currentTarget.style.display = 'none'; }} /> : null}
      <div>
        <Eyebrow>{item.pinned ? 'Pinned' : 'Update'}</Eyebrow>
        <h3>{item.title}</h3>
        <p>{item.body}</p>
      </div>
    </article>
  );
}

function MatchesScreen({ data, routeHash = location.hash, liveScores, selectedTeamId, onClearTeam }) {
  const selectedTeam = teamFrom(data, selectedTeamId, null);
  const requestedFilter = getHashParams(routeHash).get('filter') || 'all';
  const initialFilter = MATCH_FILTERS.some(([id]) => id === requestedFilter) ? requestedFilter : 'all';
  const [filter, setFilter] = useState(initialFilter);
  const [query, setQuery] = useState('');
  useEffect(() => {
    setFilter(initialFilter);
  }, [initialFilter]);
  const fixtures = useMemo(() => data.fixtures
    .filter((fixture) => fixtureHasTeam(fixture, selectedTeam?.id))
    .filter((fixture) => {
      const status = statusOf(fixture);
      if (filter === 'live') return status === 'live';
      if (filter === 'upcoming') return !RESULT_STATUSES.has(status) && status !== 'live';
      if (filter === 'results') return RESULT_STATUSES.has(status);
      return true;
    })
    .filter((fixture) => !query || [fixture.teamAName, fixture.teamAShort, fixture.teamBName, fixture.teamBShort, fixture.venueName]
      .filter(Boolean).join(' ').toLowerCase().includes(query.toLowerCase()))
    .sort((a, b) => filter === 'upcoming' ? dateValue(a) - dateValue(b) : dateValue(b) - dateValue(a)), [data.fixtures, selectedTeam?.id, filter, query]);
  const fixturesByMonth = useMemo(() => {
    const map = new Map();
    fixtures.forEach((fixture) => {
      const key = fixture.date?.slice(0, 7) || 'pending';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(fixture);
    });
    return [...map.entries()];
  }, [fixtures]);
  const changeFilter = (nextFilter) => {
    setFilter(nextFilter);
    const nextHash = nextFilter === 'all' ? '#matches' : `#matches?filter=${nextFilter}`;
    if (location.hash !== nextHash) history.replaceState(null, '', nextHash);
  };
  return (
    <div className="screen">
      <PageHeading eyebrow={shortSeason(data.season)} title="Matches" text={selectedTeam?.id ? `${selectedTeam.shortName || selectedTeam.name} fixtures, live scores and results.` : 'Live scores, upcoming fixtures and completed results.'} />
      <div className="toolbar"><div className="segmented">{MATCH_FILTERS.map(([id, label]) => <button type="button" className={filter === id ? 'active' : ''} key={id} onClick={() => changeFilter(id)}>{label}</button>)}</div><label className="search"><Search size={18} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search teams or venue" /></label></div>
      {filter !== 'live' ? (
        <div className="month-schedule">
          {fixturesByMonth.map(([month, monthFixtures]) => (
            <section className="month-group" key={month}>
              <SectionTitle eyebrow={filter === 'upcoming' ? 'Schedule' : filter === 'results' ? 'Results' : 'Season'} title={month === 'pending' ? 'Date pending' : formatMonth(`${month}-01`)} />
              <div className="match-grid">{monthFixtures.map((fixture) => <MatchCard key={fixture.id} fixture={fixture} data={data} liveScores={liveScores} />)}</div>
            </section>
          ))}
        </div>
      ) : <div className="match-grid">{fixtures.map((fixture) => <MatchCard key={fixture.id} fixture={fixture} data={data} liveScores={liveScores} />)}</div>}
      {!fixtures.length && <EmptyState title="No matches found" text="Try a different match filter or search." />}
    </div>
  );
}

const MATCH_FILTERS = [['all', 'All'], ['live', 'Live'], ['upcoming', 'Upcoming'], ['results', 'Results']];
const TEAM_STATS_TABS = [
  ['standings', 'Standings'],
  ['batting', 'Batting'],
  ['bowling', 'Bowling'],
  ['fielding', 'Fielding'],
  ['mvp', 'MVP'],
  ['matches', 'Match summary'],
];
const PLAYER_LEADERBOARD_COLUMNS = {
  batting: [
    ['Inns', (row) => statValue(row, ['innings', 'matches'])],
    ['Runs', (row) => statValue(row, ['runs'])],
    ['HS', (row) => statValue(row, ['best'])],
    ['Avg', (row) => statValue(row, ['average'])],
    ['SR', (row) => statValue(row, ['strikeRate'])],
  ],
  bowling: [
    ['Inns', (row) => statValue(row, ['innings', 'matches'])],
    ['Wkts', (row) => statValue(row, ['wickets'])],
    ['Eco', (row) => statValue(row, ['economy'])],
    ['Avg', (row) => statValue(row, ['average'])],
    ['Dots', (row) => statValue(row, ['dots'])],
  ],
  fielding: [
    ['Mat', (row) => statValue(row, ['matches'])],
    ['Dis', (row) => statValue(row, ['dismissals'])],
    ['Ct', (row) => statValue(row, ['catches'])],
    ['RO', (row) => statValue(row, ['runOuts'])],
    ['St', (row) => statValue(row, ['stumpings'])],
  ],
  mvp: [
    ['Pts', (row) => statValue(row, ['points'])],
    ['Bat', (row) => statValue(row, ['bat'])],
    ['Bowl', (row) => statValue(row, ['bowl'])],
    ['Field', (row) => statValue(row, ['field'])],
  ],
};

function statValue(row, keys) {
  return keys.map((key) => row?.[key]).find((value) => value !== undefined && value !== null && value !== '') ?? '—';
}

function statTeamFromRow(data, row) {
  const clean = (value) => String(value || '').trim().toLowerCase();
  const teamId = row?.teamId || '';
  if (teamId) {
    const team = teamFrom(data, teamId, null);
    if (team?.id) return team;
  }
  const candidates = [row?.teamName, row?.teamShort, row?.teamShortName].map(clean).filter(Boolean);
  return data.teams.find((team) => candidates.includes(clean(team.name)) || candidates.includes(clean(team.shortName))) || null;
}

function matchFilterForFixture(fixture, filter) {
  const status = statusOf(fixture);
  if (filter === 'live') return status === 'live';
  if (filter === 'upcoming') return !RESULT_STATUSES.has(status) && status !== 'live';
  if (filter === 'results') return RESULT_STATUSES.has(status);
  return true;
}

function fixtureTeamLabel(data, fixture, side) {
  const id = side === 'A' ? fixture.teamAId : fixture.teamBId;
  const team = teamFrom(data, id, {});
  return team.shortName || team.short || team.name || (side === 'A' ? fixture.teamAShort || fixture.teamAName : fixture.teamBShort || fixture.teamBName) || `Team ${side}`;
}

function pointsLine(summary, side) {
  const points = summary?.[side] || {};
  if (!summary) return '—';
  const base = Number(points.base || 0);
  const ruleBonus = Number(points.ruleBonus || 0);
  const manualBonus = Number(points.manualBonus || 0);
  const total = Number(points.total ?? base + ruleBonus + manualBonus);
  const bonus = ruleBonus + manualBonus;
  return `${total} pts${bonus ? ` · bonus ${bonus}` : ''}`;
}

function compactPoints(summary, side) {
  if (!summary) return '—';
  const points = summary?.[side] || {};
  return String(Number(points.total ?? 0));
}

function compactBonus(summary, side) {
  if (!summary) return '—';
  const points = summary?.[side] || {};
  return String(Number(points.ruleBonus || 0) + Number(points.manualBonus || 0));
}

function MatchSummaryTable({ data }) {
  const rows = [...(data.fixtures || [])]
    .filter((fixture) => statusOf(fixture) === 'live' || RESULT_STATUSES.has(statusOf(fixture)))
    .sort((a, b) => dateValue(b) - dateValue(a));
  if (!rows.length) return <EmptyState title="No match summaries yet" text="Scores and points appear here once matches start or results arrive." />;
  return (
    <section className="match-summary-section">
      <SectionTitle eyebrow="Season control" title="Match summary" />
      <div className="match-summary-table">
        <div className="match-summary-table__head">
          <span>Match</span>
          <span>Teams</span>
          <span>Scores</span>
          <span>Points</span>
          <span>Bonus</span>
          <span>Venue</span>
        </div>
        {rows.map((fixture) => {
          const status = statusOf(fixture);
          const result = fixture.externalResult || {};
          const teamA = fixtureTeamLabel(data, fixture, 'A');
          const teamB = fixtureTeamLabel(data, fixture, 'B');
          const matchLabel = fixture.matchNo ? `#${fixture.matchNo}` : 'Match';
          return (
            <a className="match-summary-table__row" href={matchCardHref(fixture)} key={fixture.id}>
              <span>
                <b>{matchLabel}</b>
                <small>{formatDate(fixture.date)}{fixture.startTime ? ` · ${formatTime(fixture.startTime)}` : ''}</small>
              </span>
              <span>
                <b>{teamA} vs {teamB}</b>
                {result.resultText ? <small>{result.resultText}</small> : <small>{fixtureStatusLabel(fixture, status)}</small>}
              </span>
              <span>
                <b>{teamA}: <Score value={result.teamAScore} overs={result.teamAOvers} /></b>
                <b>{teamB}: <Score value={result.teamBScore} overs={result.teamBOvers} /></b>
              </span>
              <span>
                <b>{teamA}: {compactPoints(fixture.pointsSummary, 'teamA')}</b>
                <b>{teamB}: {compactPoints(fixture.pointsSummary, 'teamB')}</b>
              </span>
              <span>
                <b>{teamA}: {compactBonus(fixture.pointsSummary, 'teamA')}</b>
                <b>{teamB}: {compactBonus(fixture.pointsSummary, 'teamB')}</b>
              </span>
              <span>
                <b>{fixture.venueName || fixture.groundName || '—'}</b>
                {fixture.pointsSummary?.reason ? <small>{fixture.pointsSummary.reason}</small> : <small>{pointsLine(fixture.pointsSummary, 'teamA')} · {pointsLine(fixture.pointsSummary, 'teamB')}</small>}
              </span>
            </a>
          );
        })}
      </div>
    </section>
  );
}

function LeaderboardTeam({ data, row }) {
  const team = statTeamFromRow(data, row);
  const label = team?.shortName || row.teamShort || row.teamShortName || row.teamName || '—';
  const content = <>
    <TeamMark team={team || { shortName: label, name: label }} size={24} />
    <span>{label}</span>
  </>;
  if (team?.id) {
    return <a className="leaderboard-team-link" href={`#teams/${encodeURIComponent(team.id)}`}>{content}</a>;
  }
  return <span className="leaderboard-team-link leaderboard-team-link--plain">{content}</span>;
}

function PlayerLeaderboard({ data, mode }) {
  const columns = PLAYER_LEADERBOARD_COLUMNS[mode] || [];
  const rows = data.playerStats?.[mode] || [];
  const title = mode === 'mvp' ? 'MVP leaders' : `${mode[0].toUpperCase()}${mode.slice(1)} leaders`;
  const gridStyle = {
    gridTemplateColumns: `52px minmax(220px, 2fr) minmax(120px, 1fr) repeat(${columns.length}, minmax(72px, .72fr))`,
    minWidth: `${416 + (columns.length * 72)}px`,
  };
  if (!rows.length) return <EmptyState title={`${title} are waiting`} text="Stats will populate after scorecards or imported leaderboards arrive." />;
  return (
    <section className={`leaderboard-section leaderboard-section--${mode}`}>
      <SectionTitle eyebrow="Season stats" title={title} />
      <div className="leaderboard-cards">
        {rows.map((row, index) => {
          const playerRecord = playerFromStatRow(data, row);
          const player = statValue(playerRecord, ['name', 'playerName', 'fullName']);
          const rank = statValue(row, ['rank']) === '—' ? index + 1 : statValue(row, ['rank']);
          const playerContent = row.playerId
            ? <a href={`#players/${encodeURIComponent(row.playerId)}`}>{player}</a>
            : <span>{player}</span>;
          return (
            <article className="leaderboard-card" key={`${mode}-card-${row.playerId || player}-${index}`}>
              <div className="leaderboard-card__head">
                <strong>#{rank}</strong>
                <PlayerAvatar player={playerRecord} size={38} />
                <div>
                  <h3>{playerContent}</h3>
                  <LeaderboardTeam data={data} row={row} />
                </div>
              </div>
              <div className="leaderboard-card__stats">
                {columns.map(([label, value]) => (
                  <span key={label}><b>{value(row)}</b><small>{label}</small></span>
                ))}
              </div>
            </article>
          );
        })}
      </div>
      <div className="leaderboard-table">
        <div className="leaderboard-table__head" style={gridStyle}>
          <span className="leaderboard-rank-cell">#</span>
          <span className="leaderboard-player-heading">Player</span>
          <span className="leaderboard-team-heading">Team</span>
          {columns.map(([label]) => <span className="leaderboard-stat-cell" key={label}>{label}</span>)}
        </div>
        {rows.map((row, index) => {
          const playerRecord = playerFromStatRow(data, row);
          const player = statValue(playerRecord, ['name', 'playerName', 'fullName']);
          const rank = statValue(row, ['rank']) === '—' ? index + 1 : statValue(row, ['rank']);
          const playerContent = row.playerId
            ? <a href={`#players/${encodeURIComponent(row.playerId)}`}>{player}</a>
            : <span>{player}</span>;
          return (
            <div className="leaderboard-table__row" style={gridStyle} key={`${mode}-${row.playerId || player}-${index}`}>
              <strong className="leaderboard-rank-cell">{rank}</strong>
              <span className="leaderboard-player"><PlayerAvatar player={playerRecord} size={28} />{playerContent}</span>
              <span className="leaderboard-team-cell"><LeaderboardTeam data={data} row={row} /></span>
              {columns.map(([label, value]) => <span className="leaderboard-stat-cell" key={label}>{value(row)}</span>)}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function HomeTopStandings({ data, selectedTeamId }) {
  const groups = [...(data.groupStats?.groups || [])].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  const hasGroups = groups.length > 1;
  const scopedGroups = selectedTeamId
    ? groups.map((group) => ({ ...group, rows: (group.rows || []).filter((row) => row.teamId === selectedTeamId || row.id === selectedTeamId) })).filter((group) => group.rows.length)
    : groups;
  const scopedStandings = selectedTeamId ? data.standings.filter((row) => row.teamId === selectedTeamId || row.id === selectedTeamId) : data.standings;
  const previewGroups = hasGroups ? scopedGroups.slice(0, 2) : [{ name: selectedTeamId ? 'Team standing' : 'Top standings', rows: scopedStandings }];
  const hasPreviewLimit = previewGroups.some((group) => (group.rows || []).length > 4);
  return (
    <section className="home-standings-section">
      <SectionTitle eyebrow="Season race" title={selectedTeamId ? 'Team standing' : 'Top standings'} action={<a className="text-link" href="#teams">Full table <ChevronRight size={16} /></a>} />
      {hasPreviewLimit && <p className="standings-preview-note">Showing top 4 from each group.</p>}
      <div className="home-standings-grid">
        {previewGroups.map((group) => (
          <section className="standing-group standing-group--preview" key={group.name || 'standings'}>
            <StandingHeading title={group.name || 'Group'} suffix={(group.rows || []).length > 4 ? 'Top4' : ''} stage={group.stage || 'League matches'} />
            <Standings rows={(group.rows || []).slice(0, 4)} />
          </section>
        ))}
      </div>
    </section>
  );
}

function StatusSummary({ groupStats }) {
  const byStatus = groupStats?.byStatus || {};
  const items = [
    ['Live', byStatus.live || 0, '#matches?filter=live'],
    ['Results', byStatus.complete || 0, '#matches?filter=results'],
    ['Scheduled', byStatus.scheduled || 0, '#matches?filter=upcoming'],
  ].filter(([, value]) => value > 0);
  if (!items.length) return null;
  return <section className="stat-strip" aria-label="Season fixture status">{items.map(([label, value, href]) => <a href={href} key={label}><strong>{value}</strong><span>{label}</span></a>)}</section>;
}

function TeamsScreen({ data, selectedTeamId, liveScores, onClearTeam }) {
  const selectedTeam = teamFrom(data, selectedTeamId, null);
  const [statsView, setStatsView] = useState('standings');
  if (selectedTeam?.id) return <TeamDetailScreen data={data} teamId={selectedTeam.id} liveScores={liveScores} contextTeam={selectedTeam} onClearTeam={onClearTeam} />;
  return (
    <div className="screen teams-screen">
      <PageHeading eyebrow={shortSeason(data.season)} title="Teams" text={`${data.teams.length} clubs competing this season.`} />
      <StatusSummary groupStats={data.groupStats} />
      <div className="segmented team-stats-tabs" role="tablist" aria-label="Teams and season stats">
        {TEAM_STATS_TABS.map(([id, label]) => (
          <button type="button" role="tab" aria-selected={statsView === id} className={statsView === id ? 'active' : ''} key={id} onClick={() => setStatsView(id)}>{label}</button>
        ))}
      </div>
      {statsView === 'standings'
        ? <GroupedStandings data={data} />
        : statsView === 'matches'
          ? <MatchSummaryTable data={data} />
          : <PlayerLeaderboard data={data} mode={statsView} />}
    </div>
  );
}

function DetailStat({ label, value, href, onClick }) {
  const content = <><strong>{value}</strong><span>{label}</span></>;
  if (href) return <a href={href} className="stat-link">{content}</a>;
  if (onClick) return <button type="button" className="stat-link" onClick={onClick}>{content}</button>;
  return <div>{content}</div>;
}

function TeamDetailScreen({ data, teamId, liveScores, contextTeam, onClearTeam }) {
  const team = teamFrom(data, teamId, null);
  if (!team?.id) return <NotFoundScreen title="Team not found" text="Choose a team from the standings table." backHref="#teams" backLabel="Back to teams" />;
  const standing = data.standings.find((row) => row.teamId === team.id) || {};
  const roster = (data.rosters || []).filter((row) => row.teamId === team.id).sort((a, b) => playerName(a).localeCompare(playerName(b)));
  const fixtures = useMemo(() => data.fixtures.filter((fixture) => fixture.teamAId === team.id || fixture.teamBId === team.id), [data.fixtures, team.id]);
  const preferredFilter = useMemo(() => {
    if (fixtures.some((fixture) => matchFilterForFixture(fixture, 'live'))) return 'live';
    if (fixtures.some((fixture) => matchFilterForFixture(fixture, 'upcoming'))) return 'upcoming';
    if (fixtures.some((fixture) => matchFilterForFixture(fixture, 'results'))) return 'results';
    return 'all';
  }, [fixtures]);
  const [matchFilter, setMatchFilter] = useState(preferredFilter);
  const [matchesExpanded, setMatchesExpanded] = useState(false);
  useEffect(() => {
    setMatchFilter(preferredFilter);
    setMatchesExpanded(false);
  }, [team.id, preferredFilter]);
  useEffect(() => {
    setMatchesExpanded(false);
  }, [matchFilter]);
  const filteredFixtures = useMemo(() => fixtures
    .filter((fixture) => matchFilterForFixture(fixture, matchFilter))
    .sort((a, b) => matchFilter === 'results' ? dateValue(b) - dateValue(a) : dateValue(a) - dateValue(b)), [fixtures, matchFilter]);
  const shouldLimitMatches = matchFilter !== 'live' && filteredFixtures.length > 5;
  const visibleFixtures = shouldLimitMatches && !matchesExpanded ? filteredFixtures.slice(0, 5) : filteredFixtures;
  const completed = fixtures.filter((fixture) => RESULT_STATUSES.has(statusOf(fixture))).length;
  const showResults = () => {
    setMatchFilter('results');
    document.getElementById('team-matches')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const showRoster = () => document.getElementById('team-roster')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  return (
    <div className="screen detail-screen">
      {!contextTeam?.id && <a className="back-link" href="#teams"><ArrowLeft size={17} /> Teams</a>}
      <section className="detail-hero">
        <TeamMark team={team} size={76} />
        <div>
          <Eyebrow>{team.group || team.division || shortSeason(data.season)}</Eyebrow>
          <h1>{team.shortName || team.name}</h1>
          <p>{team.name}</p>
        </div>
      </section>
      <section className="stat-strip" aria-label="Team summary">
        <DetailStat label="Rank" value={standing.rank ? `#${standing.rank}` : '—'} href="#teams" />
        <DetailStat label="Points" value={standing.points ?? 0} href="#teams" />
        <DetailStat label="Players" value={roster.length} onClick={showRoster} />
        <DetailStat label="Results" value={completed} onClick={showResults} />
      </section>
      <section id="team-matches">
        <SectionTitle eyebrow={matchFilter === 'live' ? 'Live now' : matchFilter === 'upcoming' ? 'Next up' : matchFilter === 'results' ? 'Results' : 'Schedule'} title="Team matches" />
        <div className="toolbar team-match-toolbar">
          <div className="segmented">{MATCH_FILTERS.map(([id, label]) => <button type="button" className={matchFilter === id ? 'active' : ''} key={id} onClick={() => setMatchFilter(id)}>{label}</button>)}</div>
        </div>
        <div className="match-grid">{visibleFixtures.map((fixture) => <MatchCard key={fixture.id} fixture={fixture} data={data} liveScores={liveScores} />)}</div>
        {shouldLimitMatches && <button type="button" className="button button--quiet match-more" onClick={() => setMatchesExpanded((value) => !value)}>{matchesExpanded ? 'Show less' : `Show ${filteredFixtures.length - visibleFixtures.length} more`}</button>}
        {!filteredFixtures.length && <EmptyState title="No matches found" text="Try a different match filter or search." />}
      </section>
      <section id="team-roster">
        <SectionTitle eyebrow="Roster" title="Players" />
        <div className="roster-list">
          {roster.map((row) => <a className="player-row" href={`#players/${encodeURIComponent(row.playerId)}`} key={row.rosterId || row.playerId}>
            <span className="player-avatar">{initials(row.fullName)}</span>
            <span><strong>{row.fullName}</strong><small>{row.status || 'active'}</small></span>
            <ChevronRight size={18} />
          </a>)}
        </div>
        {!roster.length && <EmptyState title="No roster published" text="Players will appear once the roster is available." />}
      </section>
    </div>
  );
}

function PlayerDetailScreen({ data, playerId }) {
  const player = (data.players || []).find((row) => row.id === playerId) || (data.rosters || []).find((row) => row.playerId === playerId);
  if (!player) return <NotFoundScreen title="Player not found" text="Choose a player from a team roster." backHref="#teams" backLabel="Back to teams" />;
  const team = teamFrom(data, player.teamId, {});
  const statGroups = Object.entries(data.playerStats || {}).map(([kind, rows]) => [
    kind,
    (rows || []).find((row) => String(row.playerId || '') === playerId || String(row.name || '').toLowerCase() === playerName(player).toLowerCase()),
  ]).filter(([, row]) => row);
  return (
    <div className="screen detail-screen">
      <a className="back-link" href={team.id ? `#teams/${encodeURIComponent(team.id)}` : '#teams'}><ArrowLeft size={17} /> {team.shortName || 'Team'}</a>
      <section className="detail-hero">
        <span className="player-avatar player-avatar--large">{initials(playerName(player))}</span>
        <div>
          <Eyebrow>{team.shortName || team.name || shortSeason(data.season)}</Eyebrow>
          <h1>{playerName(player)}</h1>
          <p>{team.name || 'Season roster'}</p>
        </div>
      </section>
      <section className="content-card">
        <Eyebrow>Profile</Eyebrow>
        <h3>{playerName(player)}</h3>
        <p>{team.name ? `${team.name} roster player` : 'Roster player'} · {player.status || 'active'}</p>
      </section>
      {statGroups.length ? <section><SectionTitle eyebrow="Season" title="Stats" /><div className="detail-list">{statGroups.map(([kind, row]) => <article className="content-card" key={kind}><Eyebrow>{kind}</Eyebrow><h3>{row.name || playerName(player)}</h3><p>{Object.entries(row).filter(([key]) => !['name', 'playerId', 'teamId'].includes(key)).slice(0, 5).map(([key, value]) => `${key}: ${value}`).join(' · ')}</p></article>)}</div></section> : null}
    </div>
  );
}

function FixtureDetailScreen({ data, fixtureId }) {
  const fixture = data.fixtures.find((item) => item.id === fixtureId);
  if (!fixture) return <NotFoundScreen title="Match not found" text="This fixture is not in the published season." backHref="/#matches" backLabel="Back to matches" />;

  const status = statusOf(fixture);
  const displayStatus = fixtureDisplayStatus(fixture, status);
  const brand = data.brand || {};
  const teamA = teamFrom(data, fixture.teamAId, { id: fixture.teamAId, name: fixture.teamAName, shortName: fixture.teamAShort, logoUrl: fixture.teamALogoUrl });
  const teamB = teamFrom(data, fixture.teamBId, { id: fixture.teamBId, name: fixture.teamBName, shortName: fixture.teamBShort, logoUrl: fixture.teamBLogoUrl });
  const result = fixture.externalResult || {};
  const played = RESULT_STATUSES.has(status) || status === 'live';
  const showResultText = played && result.resultText;
  const dateText = fixture.date ? `${formatDate(fixture.date, { year: true })}${fixture.startTime ? `, ${formatTime(fixture.startTime)}` : ''}` : 'Date pending';
  const liveHref = fixture.matchId ? `/live/${encodeURIComponent(fixture.matchId)}` : '';
  const streamHref = fixture.matchId ? `/ops/${encodeURIComponent(fixture.matchId)}?tool=streaming` : '';
  const startHref = `/score?fixture=${encodeURIComponent(fixture.id)}`;
  const shouldStartMatch = status !== 'live' && !RESULT_STATUSES.has(status);
  const primaryHref = shouldStartMatch ? startHref : liveHref;
  const primaryLabel = shouldStartMatch ? 'Start match' : status === 'live' ? 'Open live score' : 'View scorecard';

  return (
    <div className="screen detail-screen fixture-detail-screen">
      <a className="back-link fixture-back-link" href={matchFilterHref(fixture)}><ArrowLeft size={17} /> Matches</a>
      <section className="fixture-hero">
        <div className="fixture-hero__top">
          <div className="fixture-identity">
            {brand.logoSrc && <img className="fixture-league-logo" src={brand.logoSrc} alt="" />}
            <div>
              <Eyebrow>{shortSeason(data.season)}</Eyebrow>
              <div className="fixture-title-line">
                <span className="fixture-number">{fixture.matchNo ? `#${fixture.matchNo}` : 'Match'}</span>
                <StatusPill status={displayStatus} label={fixtureStatusLabel(fixture, status)} />
              </div>
              {(fixture.venueName || fixture.groundName) && <p>{fixture.venueName || fixture.groundName}</p>}
            </div>
          </div>
        </div>
        <div className="fixture-matchup">
          <a className="fixture-team" href={teamA.id ? `/#teams/${encodeURIComponent(teamA.id)}` : '/#teams'}>
            <TeamMark team={teamA} size={70} />
            <span>{teamA.shortName || teamA.name || 'Team A'}</span>
            {played && <Score value={result.teamAScore} overs={result.teamAOvers} />}
          </a>
          <span className="fixture-versus">vs</span>
          <a className="fixture-team" href={teamB.id ? `/#teams/${encodeURIComponent(teamB.id)}` : '/#teams'}>
            <TeamMark team={teamB} size={70} />
            <span>{teamB.shortName || teamB.name || 'Team B'}</span>
            {played && <Score value={result.teamBScore} overs={result.teamBOvers} />}
          </a>
        </div>
        {showResultText && <p className="fixture-result">{result.resultText}</p>}
        <div className="fixture-info-card">
          <div className="fixture-meta-list">
            <span><CalendarDays size={16} /> {dateText}</span>
            {(fixture.venueName || fixture.groundName) && <span><MapPin size={16} /> {fixture.venueName || fixture.groundName}</span>}
            {fixture.overs && <span><Shield size={16} /> {fixture.overs} overs</span>}
          </div>
        </div>
        <div className="fixture-actions">
          {primaryHref && <a className="button button--primary" href={primaryHref}>{primaryLabel}</a>}
          {streamHref && <a className="button button--quiet" href={streamHref}>Streaming</a>}
        </div>
      </section>
    </div>
  );
}

function NotFoundScreen({ title, text, backHref, backLabel }) {
  return <div className="screen detail-screen"><a className="back-link" href={backHref}><ArrowLeft size={17} /> {backLabel}</a><EmptyState title={title} text={text} /></div>;
}

function operationFixtureSort(a, b) {
  const liveRank = (statusOf(a) === 'live' ? 0 : 1) - (statusOf(b) === 'live' ? 0 : 1);
  if (liveRank) return liveRank;
  return dateValue(a) - dateValue(b);
}

function MatchOperationCard({ fixture }) {
  const status = statusOf(fixture);
  const started = !!fixture.matchId && !fixture.preprovisioned;
  const complete = RESULT_STATUSES.has(status);
  const matchTitle = `${fixture.matchNo ? `#${fixture.matchNo} ` : ''}${fixture.teamAShort || fixture.teamAName || 'Team A'} vs ${fixture.teamBShort || fixture.teamBName || 'Team B'}`;
  const meta = [formatDate(fixture.date), fixture.startTime && formatTime(fixture.startTime), fixture.venueName || fixture.groundName].filter(Boolean).join(' · ');
  if (complete) {
    return (
      <article className="operation-card">
        <div><StatusPill status={status} label={fixtureStatusLabel(fixture, status)} /><h3>{matchTitle}</h3><p>{meta}</p></div>
        <div className="operation-actions">{fixture.matchId && <a className="button button--quiet" href={`/live/${encodeURIComponent(fixture.matchId)}`}>Scorecard</a>}</div>
      </article>
    );
  }
  return (
    <article className="operation-card">
      <div>
        <StatusPill status={status} label={started ? 'Started' : 'Not started'} />
        <h3>{matchTitle}</h3>
        <p>{started ? 'Score, stream, or open the public live view.' : 'Start scoring first. Streaming unlocks after the match starts.'}</p>
        <small>{meta}</small>
      </div>
      <div className="operation-actions">
        {started ? (
          <>
            <a className="button button--primary" href={`/ops/${encodeURIComponent(fixture.matchId)}?tool=scoring`}>Score</a>
            <a className="button button--quiet" href={`/ops/${encodeURIComponent(fixture.matchId)}?tool=streaming`}>Stream</a>
            <a className="button button--quiet" href={`/live/${encodeURIComponent(fixture.matchId)}`}>Live</a>
          </>
        ) : (
          <>
            <a className="button button--primary" href={`/score?fixture=${encodeURIComponent(fixture.id)}`}>Start scoring</a>
            <span className="operation-note"><Tv size={14} /> Stream after start</span>
          </>
        )}
      </div>
    </article>
  );
}

function MatchOperations({ data, account }) {
  if (account?.status === 'loading') return <EmptyState title="Loading match access" text="Checking the teams connected to your account." />;
  if (!account?.user) return <EmptyState title="Sign in for match tools" text="Match operations are only shown to signed-in team members and league administrators." />;
  const isAdmin = (account.user.roles || []).some((role) => role.role === 'global_admin');
  const allowedTeamIds = new Set((account.teamAccess || [])
    .filter((access) => access.seasonId === data.season.id)
    .map((access) => access.teamId));
  if (!isAdmin && !allowedTeamIds.size) {
    return <EmptyState title="No team match access" text="Your account is not on an active roster for this season. Ask a league administrator to connect your player profile." />;
  }
  const fixtures = [...(data.fixtures || [])]
    .filter((fixture) => isAdmin || allowedTeamIds.has(fixture.teamAId) || allowedTeamIds.has(fixture.teamBId))
    .sort(operationFixtureSort);
  const active = fixtures.filter((fixture) => !RESULT_STATUSES.has(statusOf(fixture))).slice(0, 6);
  const recentComplete = fixtures.filter((fixture) => RESULT_STATUSES.has(statusOf(fixture)) && fixture.matchId).slice(-2);
  const visible = active.length ? active : recentComplete;
  if (!visible.length) return <EmptyState title="No match operations" text="No startable or active fixtures are available for this season yet." />;
  return <div className="operations-list">{visible.map((fixture) => <MatchOperationCard fixture={fixture} key={fixture.id} />)}</div>;
}

function MoreScreen({ data, account }) {
  const groups = [
    ['Announcements', data.announcements || [], (item) => <AnnouncementCard item={item} key={item.id} />],
    ['Practice schedule', data.practiceSessions || [], (item) => <article className="content-card" key={item.id}><Eyebrow>{formatDate(item.date, { year: true })}</Eyebrow><h3>{item.title || 'Practice session'}</h3><p>{[item.teamName, item.venueName, item.startTime && formatTime(item.startTime)].filter(Boolean).join(' · ')}</p></article>],
    ['Documents', data.documents || [], (item) => <a className="content-card content-card--link" key={item.id} href={item.downloadUrl}><Info size={22} /><div><h3>{item.title}</h3><p>Version {item.version} · Effective {formatDate(item.effectiveDate, { year: true })}</p></div><ChevronRight size={18} /></a>],
  ];
  return <div className="screen more-screen"><PageHeading eyebrow={shortSeason(data.season)} title="More" text="Scoring, streaming and league resources." /><section><SectionTitle eyebrow="Match tools" title="My match operations" /><MatchOperations data={data} account={account} /></section>{groups.map(([title, items, render]) => items.length ? <section key={title}><SectionTitle eyebrow="League" title={title} /><div className="card-grid">{items.map(render)}</div></section> : null)}</div>;
}

function PageHeading({ eyebrow, title, text }) { return <div className="page-heading"><Eyebrow>{eyebrow}</Eyebrow><h1>{title}</h1><p>{text}</p></div>; }
function EmptyState({ title, text }) { return <div className="empty-state"><Shield size={30} /><h3>{title}</h3><p>{text}</p></div>; }
function LoadingScreen() { return <div className="loading-screen"><span className="loader" /><p>Loading the season…</p></div>; }
function ErrorScreen({ message }) { return <div className="loading-screen loading-screen--error"><Info /><h1>League center unavailable</h1><p>{message}</p><button type="button" className="button button--primary" onClick={() => location.reload()}>Try again</button></div>; }

let googleIdentityScriptPromise: Promise<any> | undefined;

function loadGoogleIdentityServices() {
  if (window.google?.accounts?.id) return Promise.resolve(window.google);
  if (googleIdentityScriptPromise) return googleIdentityScriptPromise;
  googleIdentityScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => resolve(window.google);
    script.onerror = () => reject(new Error('Google Sign-In could not be loaded. Try again or use your password.'));
    document.head.append(script);
  });
  return googleIdentityScriptPromise;
}

function loginDestination(user) {
  const requested = new URLSearchParams(location.search).get('next') || '';
  if (requested.startsWith('/') && !requested.startsWith('//')) return requested;
  return (user?.roles || []).some((role) => role.role === 'global_admin') ? '/admin' : '/';
}

function authErrorMessage(error) {
  const messages = {
    'active-player-or-user-required': 'This Google account is not linked to an active ICAT player or user.',
    'google-account-already-linked': 'This ICAT account is already linked to a different Google account.',
    'google-credential-invalid': 'Google could not verify this sign-in. Please try again.',
    'google-identity-not-approved': 'This Google account could not be approved for sign-in.',
    'google-sign-in-timeout': 'Google verification timed out. Please try again.',
  };
  return messages[error] || error || 'Could not sign in with Google';
}

function LoginScreen({ dark, setDark }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [googleClientId, setGoogleClientId] = useState('');
  const googleButton = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    getJson('/api/auth/me', controller.signal)
      .then((body) => setGoogleClientId(String(body.googleClientId || '')))
      .catch(() => {});
    return () => controller.abort();
  }, []);
  useEffect(() => {
    if (!googleClientId || !googleButton.current) return undefined;
    let cancelled = false;
    loadGoogleIdentityServices().then((google) => {
      if (cancelled || !google?.accounts?.id || !googleButton.current) return;
      googleButton.current.textContent = '';
      google.accounts.id.initialize({
        client_id: googleClientId,
        auto_select: false,
        callback: async ({ credential }) => {
          setSubmitting(true);
          setError('');
          try {
            const response = await fetch('/api/auth/google', {
              method: 'POST',
              headers: { 'content-type': 'application/json', accept: 'application/json' },
              body: JSON.stringify({ credential }),
            });
            const body = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(authErrorMessage(body.error));
            location.href = loginDestination(body.user);
          } catch (err) {
            setError(err.message || 'Could not sign in with Google');
            setSubmitting(false);
          }
        },
      });
      google.accounts.id.renderButton(googleButton.current, {
        type: 'standard',
        theme: dark ? 'filled_black' : 'outline',
        size: 'large',
        shape: 'rectangular',
        text: 'continue_with',
        width: Math.min(360, googleButton.current.clientWidth || 360),
      });
    }).catch((err) => {
      if (!cancelled) setError(err.message);
    });
    return () => { cancelled = true; };
  }, [dark, googleClientId]);
  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'Could not sign in');
      location.href = loginDestination(body.user);
    } catch (err) {
      setError(err.message || 'Could not sign in');
      setSubmitting(false);
    }
  };
  return (
    <div className="login-page">
      <header className="login-topbar">
        <a className="brand" href="/" aria-label="ICAT Cricket home">
          <img src="/client/assets/icat-primary.jpeg" alt="" />
          <span><b>ICAT</b><small>Cricket</small></span>
        </a>
        <button type="button" className="theme-link" aria-label={dark ? 'Use light mode' : 'Use night mode'} title={dark ? 'Light mode' : 'Night mode'} onClick={() => setDark((value) => !value)}>
          {dark ? <Sun size={19} /> : <Moon size={19} />}
        </button>
      </header>
      <main className="login-shell">
        <section className="login-card">
          <div className="login-panel">
            <div className="login-heading">
              <Eyebrow>Account</Eyebrow>
              <h2>Sign in</h2>
              <p>Continue to your league, team, and match-day tools.</p>
            </div>
            {googleClientId && <>
              <div className="google-signin" ref={googleButton} aria-label="Continue with Google" />
              <div className="login-divider"><span>or use your password</span></div>
            </>}
            <form className="login-form" onSubmit={submit}>
              <label><span>Email address</span><input type="email" autoComplete="email" placeholder="you@example.com" required value={email} onChange={(event) => setEmail(event.target.value)} /></label>
              <label><span>Password</span><input type="password" autoComplete="current-password" placeholder="Enter your password" required value={password} onChange={(event) => setPassword(event.target.value)} /></label>
              {error && <p className="login-error" role="alert">{error}</p>}
              <button type="submit" className="button button--primary" disabled={submitting}>{submitting ? 'Signing in...' : 'Sign in'}</button>
            </form>
            <p className="login-assurance">Your sign-in is encrypted and your password is never displayed.</p>
          </div>
        </section>
      </main>
    </div>
  );
}

function ViewerSetup({ data, selectedTeamId, onSeasonChange, onTeamChange, onComplete }) {
  const [draftTeamId, setDraftTeamId] = useState(selectedTeamId || '');
  const [step, setStep] = useState('season');
  useEffect(() => {
    setDraftTeamId((current) => current && data.teams.some((team) => team.id === current) ? current : '');
  }, [data.season?.id, data.teams]);
  const save = () => {
    onTeamChange(draftTeamId);
    onComplete();
  };
  const chooseSeason = (seasonId) => {
    setDraftTeamId('');
    if (seasonId !== data.season?.id) onSeasonChange(seasonId);
    setStep('team');
  };
  return (
    <div className="setup-scrim" role="dialog" aria-modal="true" aria-labelledby="viewer-setup-title">
      <section className="setup-sheet">
        <div className="setup-sheet__body">
          <Eyebrow>League Center</Eyebrow>
          <h2 id="viewer-setup-title">{step === 'season' ? 'Choose season' : 'Choose team'}</h2>
          <p>{step === 'season' ? 'Start with the tournament season you want to view.' : `Now choose a team for ${shortSeason(data.season)}, or continue league-wide.`}</p>
          {step === 'season' ? (
            <div className="setup-step">
              <span>Season</span>
              <div className="setup-option-list">
                {data.seasons.map((season) => <button type="button" className={season.id === data.season?.id ? 'active' : ''} key={season.id} onClick={() => chooseSeason(season.id)}>{season.leagueShortName || season.leagueName || 'League'} · {shortSeason(season)}</button>)}
              </div>
            </div>
          ) : (
            <div className="setup-step">
              <span>{shortSeason(data.season)} teams</span>
              <button type="button" className={!draftTeamId ? 'active setup-wide-option' : 'setup-wide-option'} onClick={() => setDraftTeamId('')}>All teams</button>
              <div className="setup-team-grid">
                {data.teams.map((team) => <button type="button" className={team.id === draftTeamId ? 'active' : ''} key={team.id} onClick={() => setDraftTeamId(team.id)}>{team.shortName || team.name}</button>)}
              </div>
            </div>
          )}
          <div className="setup-actions">
            {step === 'team' ? <button type="button" className="button button--quiet" onClick={() => setStep('season')}>Back</button> : <button type="button" className="button button--quiet" onClick={() => { setDraftTeamId(''); onTeamChange(''); onComplete(); }}>Skip</button>}
            {step === 'season' ? <button type="button" className="button button--primary" onClick={() => setStep('team')}>Next</button> : <button type="button" className="button button--primary" onClick={save}>Save view</button>}
          </div>
        </div>
      </section>
    </div>
  );
}

export function App() {
  const params = new URLSearchParams(location.search);
  const [seasonId, setSeasonId] = useState(() => params.get('seasonId') || localStorage.getItem(VIEWER_SEASON_KEY) || '');
  const [selectedTeamId, setSelectedTeamId] = useState(() => localStorage.getItem(VIEWER_TEAM_KEY) || '');
  const [setupOpen, setSetupOpen] = useState(() => localStorage.getItem(VIEWER_SETUP_KEY) !== 'true');
  const [routeHash, setRouteHash] = useState(location.hash || '#home');
  const [pathName, setPathName] = useState(location.pathname);
  const { dark, setDark } = useLeagueTheme();
  const account = useViewerAccount();
  const accountPreferencesHydrated = useRef(false);
  const loginRoute = pathName === '/login' || pathName === '/login/';
  const pathFixtureId = fixtureIdFromPath(pathName);
  const route = pathFixtureId ? 'fixture' : getRoute(routeHash);
  const league = useLeagueData(seasonId);
  useEffect(() => {
    const onNavigation = () => {
      setRouteHash(location.hash || '#home');
      setPathName(location.pathname);
      window.scrollTo({ top: 0, behavior: 'instant' });
    };
    addEventListener('hashchange', onNavigation);
    addEventListener('popstate', onNavigation);
    return () => {
      removeEventListener('hashchange', onNavigation);
      removeEventListener('popstate', onNavigation);
    };
  }, []);
  const data = useLogoEnrichedData(league.data);
  const liveScores = useLiveScoreSummaries(data);
  const saveAccountPreferences = (nextSeasonId, nextTeamId) => {
    if (!account.user) return;
    fetch('/api/auth/viewer-preferences', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ seasonId: nextSeasonId || '', teamId: nextTeamId || '' }),
    }).catch(() => {});
  };
  const onSeasonChange = (nextId) => {
    const url = new URL(location.href);
    url.searchParams.set('seasonId', nextId);
    history.replaceState(null, '', url);
    localStorage.setItem(VIEWER_SEASON_KEY, nextId);
    localStorage.removeItem(VIEWER_TEAM_KEY);
    setSeasonId(nextId);
    setSelectedTeamId('');
    saveAccountPreferences(nextId, '');
  };
  const onTeamChange = (nextId) => {
    if (nextId) localStorage.setItem(VIEWER_TEAM_KEY, nextId);
    else localStorage.removeItem(VIEWER_TEAM_KEY);
    setSelectedTeamId(nextId);
    saveAccountPreferences(seasonId || data?.season?.id || '', nextId);
  };
  const onClearTeam = () => onTeamChange('');
  const completeSetup = () => {
    localStorage.setItem(VIEWER_SETUP_KEY, 'true');
    setSetupOpen(false);
  };
  useEffect(() => {
    if (account.status !== 'ready' || accountPreferencesHydrated.current) return;
    accountPreferencesHydrated.current = true;
    if (!account.user) return;
    const saved = account.viewerPreferences;
    if (saved?.seasonId) {
      localStorage.setItem(VIEWER_SEASON_KEY, saved.seasonId);
      setSeasonId(saved.seasonId);
      if (saved.teamId) localStorage.setItem(VIEWER_TEAM_KEY, saved.teamId);
      else localStorage.removeItem(VIEWER_TEAM_KEY);
      setSelectedTeamId(saved.teamId || '');
      localStorage.setItem(VIEWER_SETUP_KEY, 'true');
      setSetupOpen(false);
    } else if (seasonId || selectedTeamId) {
      saveAccountPreferences(seasonId, selectedTeamId);
    }
  }, [account.status, account.user, account.viewerPreferences]);
  useEffect(() => {
    if (!data?.season) return;
    if (!seasonId && data.season.id) localStorage.setItem(VIEWER_SEASON_KEY, data.season.id);
    if (selectedTeamId && data.season.id === seasonId && !data.teams.some((team) => team.id === selectedTeamId)) onTeamChange('');
  }, [data?.season?.id, data?.teams, seasonId, selectedTeamId]);
  if (loginRoute) return <LoginScreen dark={dark} setDark={setDark} />;
  if (league.status === 'loading' && !league.data) return <LoadingScreen />;
  if (league.status === 'error') return <ErrorScreen message={league.error} />;
  if (!data?.season) return <ErrorScreen message="No tournament season has been published." />;
  applyScopedTheme(themeScopeForData(data));
  const [, detailId] = getRouteParts(routeHash);
  const Screen = pathFixtureId
    ? () => <FixtureDetailScreen data={data} fixtureId={pathFixtureId} liveScores={liveScores} />
    : route === 'teams' && detailId
    ? () => <TeamDetailScreen data={data} teamId={decodeURIComponent(detailId)} liveScores={liveScores} />
    : route === 'players' && detailId
      ? () => <PlayerDetailScreen data={data} playerId={decodeURIComponent(detailId)} />
      : { home: HomeScreen, matches: MatchesScreen, table: TeamsScreen, teams: TeamsScreen, more: MoreScreen }[route];
  const navRoute = route === 'fixture' ? 'matches' : route;
  return (
    <LeagueCenterShell
      header={<AppHeader data={data} account={account} onSeasonChange={onSeasonChange} selectedTeamId={selectedTeamId} onTeamChange={onTeamChange} onClearTeam={onClearTeam} dark={dark} setDark={setDark} />}
      desktopNav={<MainNav route={navRoute} />}
      mobileNav={<MainNav route={navRoute} mobile />}
    >
      <Screen data={data} routeHash={routeHash} liveScores={liveScores} selectedTeamId={selectedTeamId} onClearTeam={onClearTeam} account={account} />
      {setupOpen && <ViewerSetup data={data} selectedTeamId={selectedTeamId} onSeasonChange={onSeasonChange} onTeamChange={onTeamChange} onComplete={completeSetup} />}
    </LeagueCenterShell>
  );
}
